import { useStacklineTheme } from '@stackline/ui';
import React from 'react';
import { BeaconProSidebarFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import GenericSidebarFilter, {
  GenericSidebarFilterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';
import {
  FILTER_OPERATION,
  useSavedSearchBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useSavedSearchBuilder';
import { SlSidebarScrollableContainer } from 'src/components/BeaconV2Components/SlSidebar';
import { useAppSelector } from 'src/utils/Hooks';

const LegacyBrandSidebarFilter = (props: GenericSidebarFilterProps) => {
  const theme = useStacklineTheme();

  const { formData, updateSavedSearch } = useSavedSearchBuilder();
  const selectedBrands = formData.termFilters.brandId.values.map((value) => ({ name: value.n, id: value.i }));
  const brandOptions = useAppSelector((state) =>
    state.brandsFollowing
      .filter((brand) => brand?.id !== 0)
      .map((brand) => ({
        ...brand,
        selected: selectedBrands.some((selectedBrand) => Number(selectedBrand.id) === brand.id)
      }))
  );

  /**
   * Search input for filtering down brands
   */
  const [currentInput, setCurrentInput] = React.useState<string>('');

  /**
   * Handles the selection of a brand.
   */
  const handleItemSelect = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      updateSavedSearch({
        key: BeaconProSidebarFilters.Brand.filterKey,
        newValue: value,
        operation: FILTER_OPERATION.Toggle
      });
    },
    [updateSavedSearch]
  );

  /**
   * Renders the list of items as checkboxes.
   */
  const renderItems = React.useCallback(() => {
    return (
      <SlSidebarScrollableContainer>
        {brandOptions
          ?.filter((brand) => brand.name.toLowerCase().includes(currentInput.toLowerCase()))
          .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1)) // sort by checked items
          .map((brand, index) => (
            <CheckboxItem
              onClick={handleItemSelect}
              value={brand.id}
              key={index}
              label={brand.name}
              theme={theme}
              checked={brand.selected}
            />
          ))}
      </SlSidebarScrollableContainer>
    );
  }, [brandOptions, currentInput, theme, handleItemSelect]);

  /**
   * Handles the change of the input to filter down the brands.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  /**
   * Clears the selected brands
   */
  const handleClear = () => {
    updateSavedSearch({
      key: BeaconProSidebarFilters.Brand.filterKey,
      operation: FILTER_OPERATION.Clear
    });

    setCurrentInput('');
  };

  return (
    <GenericSidebarFilter
      {...props}
      renderItems={renderItems}
      handleClear={handleClear}
      includeClear={selectedBrands?.length > 0}
      sidebarInputProps={{
        ...props.sidebarInputProps,
        value: currentInput,
        onChange: handleChange
      }}
    />
  );
};

export default LegacyBrandSidebarFilter;
